import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../store';
import { useLightMode } from '../utils';
import { PaymentsService } from '../api/PaymentsAPI';
import { DateRangeFilter } from '../components/DateRangeFilter/DateRangeFilter';
import s from '../styles/staff.module.css';
import logoLight from '../assets/images/icon-only-black.png';
import logoDark from '../assets/images/icon-only-bronze.png';

const money = (cents) =>
  `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const moneyShort = (cents) =>
  `$${((cents || 0) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

// No `timeZone` option → formats in the viewer's own browser timezone.
const fmtDateTime = (v) =>
  v ? new Date(v).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' }) : '—';

const fmtApptDate = (v) =>
  v ? new Date(v).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

// Render a list of strings stacked vertically (one per line), or a dash.
const stacked = (items) =>
  items && items.length
    ? items.map((t, i) => <div key={i}>{t}</div>)
    : '—';

// Admin-only picker to link an order to one of the customer's appointments.
// Uses a fixed-position panel so it isn't clipped by the table's overflow.
function AppointmentSelect({ row, light, onChange }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);

  const { data } = useQuery({
    queryKey: ['customer-appts', row.customerId],
    queryFn: () => PaymentsService.customerAppointments(row.customerId),
    enabled: open && !!row.customerId,
    staleTime: 60000,
  });
  const appts = data?.appointments || [];

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ left: r.left, top: r.bottom + 4, width: Math.max(r.width, 300) });
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const insideDropdown = (t) => t && t.nodeType === 1 && t.closest && t.closest('[data-appt-dropdown]');
    const onScroll = (e) => { if (!insideDropdown(e.target)) close(); };
    const onDoc = (e) => {
      if (btnRef.current && !btnRef.current.contains(e.target) && !insideDropdown(e.target)) close();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    document.addEventListener('mousedown', onDoc);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [open]);

  const select = (bookId) => { onChange(row.orderId, bookId); setOpen(false); };

  return (
    <>
      <button ref={btnRef} className={s.linkApptBtn} onClick={toggle} disabled={!row.customerId} type="button">
        {row.bookId
          ? `Linked ✓ — ${fmtApptDate(row.appointment?.startAt)} – ${moneyShort(row.appointment?.totalAmount)}`
          : 'Link appointment'}
      </button>
      {open && pos && (
        <div
          data-appt-dropdown
          className={`${s.apptDropdown} ${light ? s.apptDropdownLight : ''}`}
          style={{ position: 'fixed', left: pos.left, top: pos.top, minWidth: pos.width }}
        >
          <div className={s.apptOption} onClick={() => select(null)}>— none —</div>
          {appts.map((a) => (
            <div
              key={a.squareId}
              className={`${s.apptOption} ${a.squareId === row.bookId ? s.apptOptionActive : ''}`}
              onClick={() => select(a.squareId)}
            >
              <div className={s.apptOptRow}><span>{fmtApptDate(a.startAt)}</span><span>{a.locationName || '—'}</span></div>
              <div className={s.apptOptRow}><span>{a.customer || '—'}</span><span>{money(a.totalAmount)}</span></div>
              <div className={s.apptOptLabel}>Services:</div>
              <ul className={s.apptOptList}>
                {(a.services || []).map((sv, i) => <li key={i}>{sv}</li>)}
              </ul>
              <div className={s.apptOptAssigned}>AssignedBy: {a.assignedStaff?.length ? a.assignedStaff.join(', ') : '—'}</div>
            </div>
          ))}
          {!appts.length && <div className={s.apptOption} style={{ opacity: 0.6 }}>No appointments for this customer</div>}
        </div>
      )}
    </>
  );
}

export function TransactionsPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [lightMode, toggleLightMode] = useLightMode();
  const isAdmin = user?.role === 'admin';

  const [range, setRange] = useState({ start: '', end: '' });
  const onRangeChange = useCallback((r) => setRange(r), []);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['transactions', range.start, range.end],
    queryFn: () => PaymentsService.list(range.start, range.end),
    enabled: !!range.start && !!range.end,
    placeholderData: (prev) => prev,
  });
  const payments = data?.payments || [];

  const [unlinkedOnly, setUnlinkedOnly] = useState(false);
  const visiblePayments = unlinkedOnly ? payments.filter((p) => !p.bookId) : payments;

  const handleSetAppointment = useCallback(async (orderId, bookId) => {
    if (!orderId) return;
    try {
      await PaymentsService.setAppointment(orderId, bookId);
      await refetch();
    } catch (err) {
      console.error('Link appointment failed:', err?.error || err?.message || err);
    }
  }, [refetch]);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className={lightMode ? `${s.page} ${s.lightPage}` : s.page}>
      <header className={s.header}>
        <span className={s.logo}>
          <img className={s.logoImg} src={lightMode ? logoLight : logoDark} alt="ACE CAR SPA" />
        </span>
        <div className={s.headerActions}>
          <Link to="/dashboard" className={s.linkBtn}>← Dashboard</Link>
          <button onClick={() => toggleLightMode(l => !l)} className={s.linkBtn}>
            {lightMode ? 'Dark Mode' : 'Light Mode'}
          </button>
          <button onClick={handleLogout} className={s.linkBtn}>Logout</button>
        </div>
      </header>

      <div className={s.body} style={{ gridTemplateColumns: '1fr' }}>
        <div>
          <div className={s.tableHeaderRow}>
            <div className={s.sectionLabel} style={{ marginBottom: 0 }}>Transactions ({visiblePayments.length})</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button
                className={`${s.filterBtn} ${unlinkedOnly ? s.filterBtnActive : ''}`}
                onClick={() => setUnlinkedOnly((v) => !v)}
              >
                Unlinked only
              </button>
              <DateRangeFilter onChange={onRangeChange} />
            </div>
          </div>

          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Transaction Payment Date</th>
                  <th>Customer</th>
                  <th>Location</th>
                  <th>Services</th>
                  <th className={s.num}>Services Subtotal</th>
                  <th className={s.num}>Discount</th>
                  <th className={s.num}>Tax</th>
                  <th className={s.num}>Total B4 Tip</th>
                  <th className={s.num}>Tip</th>
                  <th className={s.num}>Final Total</th>
                  <th>Assigned</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr><td colSpan={10} className={s.loading}>Loading…</td></tr>
                )}
                {!isLoading && visiblePayments.length === 0 && (
                  <tr><td colSpan={10} className={s.empty}>
                    {unlinkedOnly && payments.length > 0 ? 'No unlinked transactions in this range.' : 'No transactions in this range.'}
                  </td></tr>
                )}
                {!isLoading && visiblePayments.map((p) => (
                  <tr key={p.idPayment}>
                    <td>{fmtDateTime(p.createAt)}</td>
                    <td>{p.customerName || p.customerId || '—'}</td>
                    <td>{p.locationName || p.locationId || '—'}</td>
                    <td>
                      {stacked(p.services)}
                      {isAdmin && (
                        <div style={{ marginTop: 6 }}>
                          <AppointmentSelect row={p} light={lightMode} onChange={handleSetAppointment} />
                        </div>
                      )}
                    </td>
                    <td className={s.num}>{money(p.subTotal)}</td>
                    <td className={s.num}>{money(p.discount)}</td>
                    <td className={s.num}>{money(p.tax)}</td>
                    <td className={s.num}>{money(p.amount)}</td>
                    <td className={s.num}>{money(p.tip)}</td>
                    <td className={s.num}>{money(Math.max(0, p.amount + p.tip))}</td>
                    <td>{stacked(p.teamMembers)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {isFetching && !isLoading && <div className={s.loading} style={{ padding: '8px 0' }}>Updating…</div>}
        </div>
      </div>
    </div>
  );
}
